import { sendPasswordResetEmail } from 'firebase/auth';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FcGoogle } from 'react-icons/fc';
import i18n from '../../../../shared/translation/i18n';
import { appleSignIn } from '../../../api/auth/appleSignIn';
import { googleSignIn } from '../../../api/auth/googleSignIn';
import { getAuthTokenFromCustomToken } from '../../../api/auth/signInWithCustomToken';
import { createNewAccount } from '../../../api/users/createNewAccount';
import { View } from '../../../common/constants/ViewEnum';
import { useAccountCreationContext } from '../../../common/context/AccountCreationContext';
import { useAuthContext } from '../../../common/context/AuthContext';
import { trackEvent } from '../../../common/utils/analytics';
import { auth } from '../../../config/firebaseConfig';
import PrimaryButton from '../../buttons/PrimaryButton';
import SecondaryButton from '../../buttons/SecondaryButton';
import PrimaryInput from '../../inputs/PrimaryInput';
import PrimaryParagraph from '../../texts/PrimaryParagraph';
import PrimaryTitle from '../../texts/PrimaryTitle';
import { AccountCreationViewProps } from '../types';

const CredentialsView: FC<AccountCreationViewProps> = (props) => {
	const { setEmail } = useAccountCreationContext();
	const { persistAuth, } = useAuthContext();	
	const { t } = useTranslation();

	const [accountCreationEmail, setAccountCreationEmail] = useState<string>('');

	const [accountCreationLoading, setAccountCreationLoading] = useState<boolean>(false);
	const [accountCreationError, setAccountCreationError] = useState<string>('');

	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [hasEmailBeenEntered, setHasEmailBeenEntered] = useState<boolean>(false);

	const { dob, shoppingPreference, fashionStyle, items } = useAccountCreationContext();

	/*const handleNewAccountCreation = async () => {
		setEmail(accountCreationEmail);
		props.handleNextStep?.();
	};*/

	const handleNewAccountCreation = async () => {
	setAccountCreationLoading(true);
	setAccountCreationError('');

	if (accountCreationEmail.trim()) {
		trackEvent('email_input_clicked');
	} else if (hasEmailBeenEntered) {
		trackEvent('email_erase_clicked');
	} else {
		trackEvent('email_no_input_clicked');
	}

	try {
		setEmail(accountCreationEmail); // still needed for context

		const createAccountResponse = await createNewAccount({
			email: accountCreationEmail,
			dob,
			shoppingPreference,
			fashionStyle,
			items,
			uid: '', // pass '' or handle this if available in context
		});

		if (createAccountResponse.status_code === 201) {
			auth.languageCode = i18n.language || 'en';
			if (accountCreationEmail) {
				await sendPasswordResetEmail(auth, accountCreationEmail);
			}
			const tokenRes = await getAuthTokenFromCustomToken(createAccountResponse.custom_token);
			persistAuth(tokenRes.token);

			props.handleNextStep?.(); // ⬅️ proceed only if success
		} else {
			setAccountCreationError(t('account_creation_error_generic') || 'Failed to create account. Please try again.');
		}
	} catch (error) {
		console.error('Error creating account:', error);
		setAccountCreationError(t('account_creation_error_generic') || 'Failed to create account. Please try again.');
	} finally {
		setAccountCreationLoading(false);
	}
};

	const handleGoogleSignIn = async () => {
		trackEvent('google_signup_clicked');
		setAccountCreationLoading(true);
		setAccountCreationError('');
		try {
			const googleSignInResponse = await googleSignIn();

			persistAuth(googleSignInResponse.token);
			props.handleNextStep?.();
		} catch (error: any) {
			console.error('Error during login:', error);
			setAccountCreationError(t('sign_in_with_google_error') || 'An error occurred while signing in with Google.');
		} finally {
			setAccountCreationLoading(false);
		}
	};

	const handleAppleSignIn = async () => {
		setAccountCreationLoading(true);
		setAccountCreationError('');
		try {
			const appleSignInResponse = await appleSignIn();

			persistAuth(appleSignInResponse.token);
			props.handleNextStep?.();
		} catch (error: any) {
			console.error('Error during login:', error);
			setAccountCreationError(t('sign_in_with_apple_error') || 'An error occurred while signing in with Apple.');
		} finally {
			setAccountCreationLoading(false);
		}
	};

	return (

		<div className="pop-h-full pop-fadeIn pop-overflow-y-auto pop-w-full pop-hide-scrollbar">
			{showConfirmModal && (
				<div
					style={{
					position: 'fixed',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.4)',
					zIndex: 9999,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					}}
				>
					<div
					style={{
						background: 'white',
						borderRadius: '12px',
						padding: '24px',
						maxWidth: '90%',
						textAlign: 'center',
						boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
					}}
					>
					<PrimaryTitle className={'pop-text-xl pop-text-semibold pop-mb-5'}>
						{t('create_account_no_email_confirmation_title')}
					</PrimaryTitle>
					<PrimaryParagraph className={'pop-text-sm pop-font-light pop-mb-4'}>{t('create_account_no_email_confirmation_message')}</PrimaryParagraph>
					<button className={'pop-my-4 pop-underline pop-text-popsizeSecondary90 pop-font-semibold'} onClick={() => setShowConfirmModal(false)}>
						{t('create_account_no_email_confirmation_cancel')}
					</button>
					<PrimaryButton
						className={'pop-w-full pop-bg-popsizeGradientPrimary9050 pop-border-none pop-text-popsizePrimary10'}
						onClick={() => {
							setShowConfirmModal(false);
							setEmail('');
							props.handleNextStep?.();
						}}
					>
						{t('create_account_no_email_confirmation_confirm')}
					</PrimaryButton>
					</div>
				</div>
				)}
			<div className={'pop-flex pop-flex-col pop-w-full pop-px-4 pop-overflow-y-auto pop-h-full pop-mt-4'}>
				<div className={'pop-flex pop-flex-col pop-w-full pop-mb-4'}>
					<PrimaryTitle className={'pop-text-center pop-mb-2'}>{t('save_fashion_profile')}</PrimaryTitle>
					<PrimaryParagraph className={'pop-text-base pop-text-center pop-mb-4'}>
						{t('create_account_message_start')}{' '}
					</PrimaryParagraph>
					<div className={'pop-flex pop-flex-col'}>
						{/* <PrimaryParagraph className={'pop-text-lg pop-text-center pop-font-bold'}>{t('welcome_page_subtagline')}</PrimaryParagraph> */}
						<PrimaryInput
							type={'text'}
							placeholder={t('enter_email')}
							value={accountCreationEmail}
							onChange={(e) => {
								const newValue = e.target.value;
								setAccountCreationEmail(newValue);
								if (newValue.trim() && !hasEmailBeenEntered) {
									setHasEmailBeenEntered(true);
								}
							}}
							className={'pop-w-full'}
						/>
						<div className={'pop-sticky pop-bottom-0 pop-py-2 pop-bg-popsizeSecondary10 pop-w-full'}>
							{accountCreationError && (
								<PrimaryParagraph className="pop-text-popsizeAccentRed90 pop-text-sm pop-text-center pop-mb-5 pop-font-normal">
									{t('account_creation_error_1')}
									<span
												className="pop-text-popsizeSecondary90 pop-cursor-pointer hover:pop-underline pop-underline pop-font-semibold"
												onClick={() => props.toggleView && props.toggleView(View.Login)}
											  >
												{t('account_creation_error_sign_in')}
											  </span>
									{t('account_creation_error_2')}
								</PrimaryParagraph>
							)}
							<SecondaryButton
								className={'pop-w-full pop-bg-popsizeGradientSecondary9050 pop-border-none pop-text-white'}
								onClick={handleNewAccountCreation}
								isLoading={accountCreationLoading}
								isDisabled={false}
								fullButtonSecondary={true}
							>
								{t('get_my_size')}
							</SecondaryButton>
							{/*<PrimaryParagraph className={"pop-text-center pop-text-xs pop-mt-3 pop-text-popsizePrimary90 pop-font-regular"}>
								<span
									className="pop-underline pop-cursor-pointer"
									onClick={() => setShowConfirmModal(true)}
								>
									<u>{t('create_account_no_email')}</u>
								</span>
							</PrimaryParagraph>*/}
						</div>
					</div>
				</div>
					<div className={'pop-flex pop-flex-col pop-justify-center pop-items-center'}>
						{/*<PrimaryParagraph><p style={{ textTransform: 'uppercase' }}>{t('or')}</p></PrimaryParagraph>*/}
							
						<div className="pop-flex pop-flex-row pop-gap-4 pop-my-2 pop-w-full pop-justify-center">
							<SecondaryButton
								className="pop-bg-white pop-text-black pop-border-gray-300 pop-flex pop-items-center pop-justify-center pop-gap-2"
								onClick={handleGoogleSignIn}
								isDisabled={accountCreationLoading}
								//halfButtonSecondary={true}
								fullButtonSecondary={true}
								shadow="none"
								fontSize='small'
							>
								<FcGoogle size={16} />
								<span>{t('sign_in_with_google')}</span>
							</SecondaryButton>

							{/*<SecondaryButton
								className="pop-bg-black pop-text-white pop-flex pop-items-center pop-justify-center pop-gap-2 hover:pop-text-gray-950"
								onClick={handleAppleSignIn}
								isDisabled={accountCreationLoading}
								halfButtonSecondary={true}
								shadow="none"
								fontSize='small'
							>
								<FaApple size={16} />
								<span>{t('sign_in_with_apple')}</span>
							</SecondaryButton>*/}
						</div>
						<PrimaryParagraph
							className={'pop-text-center pop-italic pop-text-popsizePrimary90'}
							fontSize='verySmall'
							>
						{t('privacy_message_3')}{' '}
							<a
								href={'https://www.popsize.ai/terms-and-conditions.html'}
								target={'_blank'}
								rel="noreferrer"
								className={'pop-text-popsizePrimary50 pop-underline'}
								onClick={() => trackEvent('tc_link_clicked')}
							>
								{t('terms_and_conditions')}
							</a>{' '}
							{t('and')}{' '}
							<a
								href={'https://www.popsize.ai/privacy.html'}
								target={'_blank'}
								rel="noreferrer"
								className={'pop-text-popsizePrimary50 pop-underline'}
								onClick={() => trackEvent('privacy_link_clicked')}
							>
								{t('user_my_profile_privacy_policies')}
							</a>
						</PrimaryParagraph>
					</div>
				</div>
			</div>
	);
};

export default CredentialsView;